/**
 * The sentence a PERSON reads when `applyDdlEdit` refuses a column name the
 * source statement declares both delimited and bare.
 *
 * The thrown message names the payload field that resolves it
 * (`columnNameOrigin`) — the right answer for an API caller, and a field no
 * form exposes. Every VQE form that can reach the refusal says this instead,
 * from here rather than each in its own words, so the five surfaces cannot
 * describe one rule five ways.
 *
 * Two resolutions, because the forms differ in what the user can actually do
 * about it:
 *   - `'edit-as-sql'` — the alter-column popover names ONE column and has no
 *     field to say which occurrence; the user's move is to write the statement.
 *   - `'type-delimiters'` — a key / index / FK form takes a typed column list,
 *     and an entry there answers for itself — `decodeTypedColumnList` reads
 *     the delimiters the user typed, and the stored spelling reaches the other
 *     column — so the user's move is to type one of the two.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B1.
 */
import type { SqlEngine } from '../../../../sql-grammar/model';
import {
  ENGINE_DEFAULT_QUOTING,
  foldAsStored,
  quoteWithStyle,
} from '../../../../sql-resolver/ddl/apply/dialect.quoting';

export type AmbiguousColumnResolution = 'edit-as-sql' | 'type-delimiters';

export function ambiguousColumnMessage(
  columnName: string,
  engine: SqlEngine,
  resolution: AmbiguousColumnResolution,
): string {
  const delimited = quoteWithStyle(columnName, ENGINE_DEFAULT_QUOTING[engine]);
  const stored = foldAsStored(columnName, engine);
  // Whether the two declarations are two COLUMNS is the engine's answer, not
  // ours. They are on an engine that folds a bare name (PostgreSQL lowers,
  // Oracle uppers), because delimiters suppress the fold. On one that folds
  // nothing — SQL Server, MySQL, SQLite — the two spellings name ONE column
  // and the statement is a duplicate the server itself rejects, so saying
  // "two different columns" there would be false and there is no second
  // column to offer a spelling for.
  const folds = stored !== columnName;
  if (!folds) {
    return (
      `This statement declares “${columnName}” twice — once in delimiters and `
      + `once bare — and on this engine that is one column declared twice, `
      + `which the server will reject. Use “Edit as SQL” to fix the statement.`
    );
  }
  const problem
    = `This statement declares “${columnName}” twice — once in delimiters and `
    + `once bare — and those are two different columns. `;
  if (resolution === 'edit-as-sql') {
    return problem + `Use “Edit as SQL” to change the one you mean.`;
  }
  // The bare column is reachable by typing the name the SERVER stores it
  // under: no declaration matches those bytes, so the list falls to the
  // bare-reference default and the entry is emitted undelimited — which the
  // engine folds to exactly that column.
  return problem + `Type ${delimited} for the delimited one, or ${stored} for the other.`;
}
